import * as React from 'react'
import dayjs from 'dayjs'
import type { DateRange as DayPickerDateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import { Calendar } from '#/shared/ui/calendar'
import { CalendarIcon } from '#/shared/ui/icons'
import { Label } from '#/shared/ui/label'
import type { LabelSize } from '#/shared/ui/label'
import { Popover } from '#/shared/ui/popover'

export type DateRange = DayPickerDateRange

function buildRangeDisabled(fromDate?: Date, toDate?: Date) {
  if (!fromDate && !toDate) return undefined
  return (day: Date) => {
    if (fromDate && day < fromDate) return true
    if (toDate && day > toDate) return true
    return false
  }
}

function triggerClassName({
  hasValue,
  error,
  open,
  className,
}: {
  hasValue: boolean
  error: boolean
  open: boolean
  className?: string
}) {
  return cn(
    'h-8 w-full justify-between gap-2 rounded-md px-1.25 text-sm font-normal',
    hasValue ? 'text-gray-900' : 'text-gray-300',
    error && 'border-red-500 focus-visible:focus-ring-error',
    open && !error && 'focus-ring',
    className,
  )
}

interface TriggerProps {
  id: string
  open: boolean
  hasValue: boolean
  disabled: boolean
  error: boolean
  /**
   * Fallback accessible name for label-less pickers only. When a visible
   * `label` exists its `htmlFor` association must win — an `aria-label` here
   * would override it and make every picker announce identically.
   */
  ariaLabel?: string
  display: string
  className?: string
}

function PickerTrigger({
  id,
  open,
  hasValue,
  disabled,
  error,
  ariaLabel,
  display,
  className,
}: TriggerProps) {
  return (
    <Button
      type="button"
      id={id}
      variant="outline"
      disabled={disabled}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={ariaLabel}
      data-slot="date-picker-trigger"
      className={triggerClassName({ hasValue, error, open, className })}
    >
      <span className="truncate">{display}</span>
      <CalendarIcon className="size-3.5 shrink-0 text-gray-500" />
    </Button>
  )
}

export interface DatePickerProps {
  value?: Date
  defaultValue?: Date
  onValueChange?: (date: Date | undefined) => void
  label?: string
  labelSize?: LabelSize
  placeholder?: string
  disabled?: boolean
  error?: boolean
  className?: string
  id?: string
  dateFormat?: string
  fromDate?: Date
  toDate?: Date
  instantApply?: boolean
  align?: 'left' | 'center' | 'right'
}

function DatePickerFooter({
  draft,
  todayDisabled,
  mondayDisabled,
  instantApply,
  onToday,
  onMonday,
  onCancel,
  onApply,
}: {
  draft: Date | undefined
  todayDisabled: boolean
  mondayDisabled: boolean
  instantApply: boolean
  onToday: () => void
  onMonday: () => void
  onCancel: () => void
  onApply: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex items-stretch gap-2.5 border-t border-gray-200 p-3">
      <Button
        type="button"
        variant="outline"
        size="xs"
        className="shrink-0 px-3"
        disabled={todayDisabled}
        onClick={onToday}
      >
        {t('datePicker.today')}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="xs"
        className="shrink-0 px-3"
        disabled={mondayDisabled}
        onClick={onMonday}
      >
        {t('datePicker.monday')}
      </Button>
      {!instantApply && (
        <>
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="ml-auto w-20 shrink-0"
            onClick={onCancel}
          >
            {t('datePicker.cancel')}
          </Button>
          <Button
            type="button"
            size="xs"
            className="flex-1 px-3.5"
            disabled={!draft}
            onClick={onApply}
          >
            {t('datePicker.apply')}
          </Button>
        </>
      )}
    </div>
  )
}

function DatePickerPanel({
  initialDate,
  fromDate,
  toDate,
  instantApply,
  active,
  onCancel,
  onApply,
}: {
  initialDate: Date | undefined
  fromDate?: Date
  toDate?: Date
  instantApply: boolean
  active: boolean
  onCancel: () => void
  onApply: (date: Date | undefined) => void
}) {
  const [draft, setDraft] = React.useState<Date | undefined>(initialDate)
  const today = React.useMemo(() => dayjs().startOf('day').toDate(), [])
  const nextMonday = React.useMemo(() => {
    const start = dayjs().startOf('day')
    return start.add((1 - start.day() + 7) % 7, 'day').toDate()
  }, [])
  const beforeMin = fromDate !== undefined && today < fromDate
  const afterMax = toDate !== undefined && today > toDate
  const todayDisabled = beforeMin || afterMax
  const mondayDisabled =
    (fromDate !== undefined && nextMonday < fromDate) ||
    (toDate !== undefined && nextMonday > toDate)

  const handleSelect = (next: Date | undefined) => {
    setDraft(next)
    if (instantApply) onApply(next)
  }

  const handleToday = () => {
    setDraft(today)
    if (instantApply) onApply(today)
  }

  const handleMonday = () => {
    setDraft(nextMonday)
    if (instantApply) onApply(nextMonday)
  }

  return (
    <div className="flex flex-col">
      <Calendar
        mode="single"
        captionLayout="dropdown"
        selected={draft}
        defaultMonth={draft ?? initialDate}
        onSelect={handleSelect}
        startMonth={fromDate ?? new Date(1960, 0)}
        endMonth={toDate ?? new Date(new Date().getFullYear() + 10, 11)}
        disabled={buildRangeDisabled(fromDate, toDate)}
        autoFocus={active}
      />
      <DatePickerFooter
        draft={draft}
        todayDisabled={todayDisabled}
        mondayDisabled={mondayDisabled}
        instantApply={instantApply}
        onToday={handleToday}
        onMonday={handleMonday}
        onCancel={onCancel}
        onApply={() => onApply(draft)}
      />
    </div>
  )
}

function useControlledDate(
  controlled: Date | undefined,
  defaultValue: Date | undefined,
  onChange?: (date: Date | undefined) => void,
): [Date | undefined, (next: Date | undefined) => void] {
  const isControlled = controlled !== undefined
  const [internal, setInternal] = React.useState<Date | undefined>(defaultValue)
  const value = isControlled ? controlled : internal
  const setValue = React.useCallback(
    (next: Date | undefined) => {
      if (!isControlled) setInternal(next)
      onChange?.(next)
    },
    [isControlled, onChange],
  )
  return [value, setValue]
}

// eslint-disable-next-line complexity
export function DatePicker({
  value,
  defaultValue,
  onValueChange,
  label,
  labelSize = 'sm',
  placeholder,
  disabled = false,
  error = false,
  className,
  id,
  dateFormat = 'MMMM D, YYYY',
  fromDate,
  toDate,
  instantApply = false,
  align = 'center',
}: DatePickerProps) {
  const { t } = useTranslation()
  const reactId = React.useId()
  const triggerId = id ?? reactId
  const [date, setDate] = useControlledDate(value, defaultValue, onValueChange)

  const resolvedPlaceholder = placeholder ?? t('datePicker.placeholder')
  const displayValue = date
    ? dayjs(date).format(dateFormat)
    : resolvedPlaceholder

  return (
    <div className="flex w-full flex-col gap-0.5">
      {label && (
        <Label htmlFor={triggerId} size={labelSize}>
          {label}
        </Label>
      )}
      <Popover
        portal
        side="auto"
        align={align}
        matchTriggerWidth={false}
        trigger={(open) => (
          <PickerTrigger
            id={triggerId}
            open={open}
            hasValue={Boolean(date)}
            disabled={disabled}
            error={error}
            ariaLabel={label ? undefined : t('datePicker.ariaLabel')}
            display={displayValue}
            className={className}
          />
        )}
        wrapperClassName="w-full"
        contentClassName="overflow-hidden rounded-md border border-gray-200 bg-white shadow-popover"
      >
        {(close, open) => (
          <DatePickerPanel
            initialDate={date}
            fromDate={fromDate}
            toDate={toDate}
            instantApply={instantApply}
            active={open}
            onCancel={close}
            onApply={(next) => {
              setDate(next)
              close()
            }}
          />
        )}
      </Popover>
    </div>
  )
}

export interface DateRangePickerProps {
  value?: DateRange
  defaultValue?: DateRange
  onValueChange?: (range: DateRange | undefined) => void
  label?: string
  labelSize?: LabelSize
  placeholder?: string
  disabled?: boolean
  error?: boolean
  className?: string
  id?: string
  dateFormat?: string
  fromDate?: Date
  toDate?: Date
  numberOfMonths?: number
  /** Custom trigger, replacing the default field (e.g. a date badge). */
  trigger?: (open: boolean) => React.ReactNode
  /** Wrapper class for the popover anchor; defaults to full width. */
  wrapperClassName?: string
  /** Popover horizontal alignment relative to the trigger. */
  align?: 'left' | 'center' | 'right' | 'auto'
  /** Props for the calendar surface — e.g. `data-phone-surface` for theming. */
  contentProps?: React.HTMLAttributes<HTMLDivElement> & {
    [key: `data-${string}`]: string | undefined
  }
}

function useControlledRange(
  controlled: DateRange | undefined,
  defaultValue: DateRange | undefined,
  onChange?: (range: DateRange | undefined) => void,
): [DateRange | undefined, (next: DateRange | undefined) => void] {
  const isControlled = controlled !== undefined
  const [internal, setInternal] = React.useState<DateRange | undefined>(
    defaultValue,
  )
  const value = isControlled ? controlled : internal
  const setValue = React.useCallback(
    (next: DateRange | undefined) => {
      if (!isControlled) setInternal(next)
      onChange?.(next)
    },
    [isControlled, onChange],
  )
  return [value, setValue]
}

function formatRange(range: DateRange | undefined, dateFormat: string) {
  if (!range?.from) return undefined
  if (!range.to) return dayjs(range.from).format(dateFormat)
  return `${dayjs(range.from).format(dateFormat)} – ${dayjs(range.to).format(dateFormat)}`
}

function DateRangePanel({
  initialRange,
  fromDate,
  toDate,
  numberOfMonths,
  active,
  onCancel,
  onApply,
}: {
  initialRange: DateRange | undefined
  fromDate?: Date
  toDate?: Date
  numberOfMonths: number
  active: boolean
  onCancel: () => void
  onApply: (range: DateRange | undefined) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = React.useState<DateRange | undefined>(initialRange)
  const canApply = Boolean(draft?.from && draft.to)

  return (
    <div className="flex flex-col">
      <Calendar
        mode="range"
        captionLayout="dropdown"
        numberOfMonths={numberOfMonths}
        selected={draft}
        defaultMonth={draft?.from ?? initialRange?.from}
        onSelect={setDraft}
        startMonth={fromDate ?? new Date(1960, 0)}
        endMonth={toDate ?? new Date(new Date().getFullYear() + 10, 11)}
        disabled={buildRangeDisabled(fromDate, toDate)}
        autoFocus={active}
      />
      <div className="flex items-stretch gap-2.5 border-t border-gray-200 p-3">
        <Button
          type="button"
          variant="outline"
          size="xs"
          className="w-20 shrink-0"
          onClick={onCancel}
        >
          {t('datePicker.cancel')}
        </Button>
        <Button
          type="button"
          size="xs"
          className="flex-1 px-3.5"
          disabled={!canApply}
          onClick={() => onApply(draft)}
        >
          {t('datePicker.apply')}
        </Button>
      </div>
    </div>
  )
}

function useRangePickerState(props: DateRangePickerProps) {
  const { t } = useTranslation()
  const reactId = React.useId()
  const [range, setRange] = useControlledRange(
    props.value,
    props.defaultValue,
    props.onValueChange,
  )
  const dateFormat = props.dateFormat ?? 'MMM D, YYYY'
  const resolvedPlaceholder =
    props.placeholder ?? t('datePicker.rangePlaceholder')
  const displayValue = formatRange(range, dateFormat) ?? resolvedPlaceholder
  const triggerId = props.id ?? reactId
  const hasValue = Boolean(range?.from)
  return { t, range, setRange, displayValue, triggerId, hasValue }
}

export function DateRangePicker(props: DateRangePickerProps) {
  const {
    label,
    labelSize = 'sm',
    disabled = false,
    error = false,
    className,
    fromDate,
    toDate,
    numberOfMonths = 2,
  } = props
  const { t, range, setRange, displayValue, triggerId, hasValue } =
    useRangePickerState(props)

  const picker = (
    <Popover
      portal
      side="auto"
      align={props.align}
      matchTriggerWidth={false}
      trigger={
        props.trigger ??
        ((open) => (
          <PickerTrigger
            id={triggerId}
            open={open}
            hasValue={hasValue}
            disabled={disabled}
            error={error}
            ariaLabel={label ? undefined : t('datePicker.rangeAriaLabel')}
            display={displayValue}
            className={className}
          />
        ))
      }
      wrapperClassName={props.wrapperClassName ?? 'w-full'}
    >
      {(close, open) => (
        <div
          {...props.contentProps}
          className="overflow-hidden rounded-md border border-gray-200 bg-white text-gray-900 shadow-popover"
        >
          <DateRangePanel
            initialRange={range}
            fromDate={fromDate}
            toDate={toDate}
            numberOfMonths={numberOfMonths}
            active={open}
            onCancel={close}
            onApply={(next) => {
              setRange(next)
              close()
            }}
          />
        </div>
      )}
    </Popover>
  )

  if (!label) return picker
  return (
    <div className="flex w-full flex-col gap-0.5">
      <Label htmlFor={triggerId} size={labelSize}>
        {label}
      </Label>
      {picker}
    </div>
  )
}
